import { createSelector } from 'reselect';

import { IRetailerSlice } from '@lib/core/retailers/slices/retailer';
import { TRetailerCommunicationSetText } from '@lib/core/retailers/types';
import { STORE } from '@lib/core/service/consts';
import { AppStateType } from '@lib/core/service/types/appStateType';
import { languages } from '@lib/tools/locale/utils/consts';
import { DEFAULT_MAX_LENGTH_TASTE_PATH_PER_CATEGORY } from '@lib/tools/shared/helpers/consts';

/**
 * @returns the whole retailer state
 */
const selectRetailerState = (state): IRetailerSlice => state?.retailer;

/**
 * @returns the retailer's data
 */
export const selectRetailerData = createSelector([selectRetailerState], retailerState => retailerState?.data);

export const selectRetailerSlug = createSelector([selectRetailerData], retailerData => retailerData?.slug || '');

export const selectRetailerPrivacyUrl = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.privacy_url || '',
);
/**
 * Contains custom configs for the retailer.
 * @returns the retailer's tags
 */
export const selectRetailerTags = createSelector([selectRetailerData], retailerData => retailerData?.tags);

/**
 * @returns max taste path length for each category
 */
export const selectRetailerMaxLengthTastePathPerCategory = createSelector(
  [selectRetailerData, (state: AppStateType) => state?.retailerLocation?.data?.identifier],
  (retailerData, retailerLocationId) => {
    const maxLengthTastePathPerCategory = Number(
      retailerData?.tags?.[retailerLocationId]?.maxLengthTastePathPerCategory,
    );

    return maxLengthTastePathPerCategory || DEFAULT_MAX_LENGTH_TASTE_PATH_PER_CATEGORY;
  },
);

/**
 * @returns the url for retailer's Logo
 */
export const selectRetailerLogo = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.tags?.headerLogo || '',
);

/**
 * @returns the retailer's name
 */
export const selectRetailerName = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.name || retailerData?.slug || '',
);
/**
 * @returns the retailer's id
 */
export const selectRetailerId = createSelector([selectRetailerData], retailerData => retailerData?.identifier || '');

/**
 * @returns the retailer's languages
 */
export const selectRetailerLanguages = createSelector(
  [selectRetailerState],
  retailer => retailer?.data?.supported_languages || [],
);
/**
 * @returns the retailer's page types
 */
export const selectRetailerPageType = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.page_type || STORE,
);

/**
 * @returns the retailer's default language
 */
export const selectRetailerDefaultLanguage = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.language || languages.ENGLISH,
);

/**
 * @returns the retailer's error
 */

export const selectRetailerError = createSelector([selectRetailerState], retailer => retailer?.error);

/**
 * @returns the retailer loader.
 */
export const selectRetailerLoading = createSelector([selectRetailerState], retailer => retailer?.isLoading || false);

/**
 * @returns the retailer widget access
 */
export const selectRetailerWidgetAccess = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.widget || 'disabled',
);

/**
 * @returns state of a retailer access
 */
export const selectIsAuthenticatedRetailerAccess = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.widget === 'authenticated',
);

/**
 * @returns state of a retailer access
 */
export const selectIsAnonymousRetailerAccess = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.widget === 'anonymous',
);

/**
 * @returns the retailer custom style
 */
export const selectRetailerCustomStyle = createSelector(
  [selectRetailerData],
  retailerData => retailerData?.custom_style || null,
);

/**
 * @returns {TRetailerCommunicationSetText} the retailer communication set text object.
 */
export const selectRetailerCommunicationSetText = createSelector(
  [selectRetailerData],
  retailerData => (retailerData?.retailer_communication as { text?: TRetailerCommunicationSetText })?.text || {},
);

export const selectRetailerCommunicationStyleOrder = createSelector(
  [selectRetailerData],
  retailerData =>
    (retailerData?.retailer_communication as { text?: { STYLE_ORDER?: string[] } })?.text?.STYLE_ORDER ?? [],
);
